import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import theme from "../theme";

// Icons
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

function Footer({ handleStaffNameOnHeader }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddActive, setIsAddActive] = useState(false);

  const iconStyle = { width: 32, height: 32 };

  // Footer nav items (excluding center button)
  const footerItems = [
    {
      icon: HomeRoundedIcon,
      path: "/home",
      label: "Home",
    },
    {
      icon: ConfirmationNumberRoundedIcon,
      path: "/mytickets",
      label: "My Tickets",
    },
    {
      icon: GroupsRoundedIcon,
      path: "/team",
      label: "Team",
    },
    {
      icon: SettingsRoundedIcon,
      path: "/all-service-tickets",
      label: "Settings",
    },
  ];

  // Determine active index based on current path
  const activeIndex = footerItems.findIndex(
    (item) => location.pathname === item.path
  );

  // Sync plus/cross state with the route
  useEffect(() => {
    if (
      location.pathname === "/all-service-tickets" ||
      location.pathname === "/raise-service-ticket"
    ) {
      setIsAddActive(true); // show cross
    } else {
      setIsAddActive(false); // show plus
    }
  }, [location.pathname]);

  // Raise service ticket icon click handler
  const handleAddClick = () => {
    if (location.pathname !== "/all-service-tickets") {
      navigate("/all-service-tickets");
    } else {
      navigate("/home");
    }
  };

  // Show the center icon
  const showCenterButton = !(
    location.pathname === "/team" ||
    location.pathname === "/performance" ||
    location.pathname === "/all-stores" ||
    location.pathname === "/create-store" ||
    location.pathname === "/create-department" ||
    location.pathname === "/create-user"
  );

  return (
    <Box
      sx={{
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        height: 70,
        backgroundColor: "#fff",
        borderTop: "1px solid rgba(0,0,0,0.1)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        boxSizing: "border-box",
        position: "fixed",
      }}
    >
      {/* Navigation Row */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
        }}
      >
        {footerItems.map((item, idx) => {
          const isActive = idx === activeIndex;
          const IconComp = item.icon;
          return (
            <Box
              key={idx}
              onClick={() => {
                if (item.path === "/mytickets") {
                  handleStaffNameOnHeader(null);
                }
                navigate(item.path);
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "margin 0.3s ease-in-out",
                ...(showCenterButton && idx === 1 && { mr: 6 }),
              }}
            >
              {/* Icon */}
              <IconComp
                sx={{
                  ...iconStyle,
                  color: isActive ? theme.lightTheme.themeCol : "#a4a4a4",
                  transition: "color 0.3s ease",
                }}
              />

              {/* Label */}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: isActive ? theme.lightTheme.themeCol : "#a4a4a4",
                  transition: "color 0.3s ease, font-weight 0.3s ease",
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Center Add Button */}
      {showCenterButton && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 36,
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 57,
              height: 57,
              borderRadius: "50%",
              boxShadow: `
                  inset -2px -4px 10px ${!isAddActive ? "#183499" : "#942527"},
                  0px 7px 10px ${
                    !isAddActive
                      ? "rgba(0, 26, 119, 0.4)"
                      : "rgba(117, 0, 2, 0.4)"
                  }
                `,
              background: !isAddActive
                ? "linear-gradient(135deg, rgba(127,127,253,1) 0%, rgba(0,128,128,1) 100%)"
                : "linear-gradient(135deg, #e45265ff 0%, #f34343ff 100%)",
              cursor: "pointer",
              transition: "background 0.3s ease, transform 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:active": {
                transform: "scale(0.9)",
              },
            }}
            onClick={handleAddClick}
          >
            <AddRoundedIcon
              sx={{
                fontSize: 34,
                color: "#fff",
                transform: isAddActive ? "rotate(135deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Footer;
